import { zodResolver } from '@hookform/resolvers/zod';
import Button from '@rio-cloud/rio-uikit/Button';
import Dialog from '@rio-cloud/rio-uikit/Dialog';
import Notification from '@rio-cloud/rio-uikit/Notification';
import useAutoAnimate from '@rio-cloud/rio-uikit/useAutoAnimate';
import { useRef, useState } from 'react';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { apiSchema } from '#api/model/types.ts';
import { useCreateOrderMutation } from '#api-bridge/orderApi.ts';
import { reportErrorToSentry } from '#configuration/setup/sentry.ts';
import { CsvImportDialogFooter } from '#features/csvImport/CsvImportDialogFooter.tsx';
import { CsvImportFilePicker } from '#features/csvImport/CsvImportFilePicker.tsx';
import { CsvImportInstructions } from '#features/csvImport/CsvImportInstructions.tsx';
import { ImportOrderDetails } from '#features/csvImport/ImportOrderDetails.tsx';
import { type ImportOrderFormData, ImportOrderFormDataSchema } from '#features/csvImport/ImportOrderFormValidation.ts';
import { mapWriteOrder } from '#utils/orders.util.ts';

import { readFileContent } from './csvFileReader.ts';
import { parseCsvImport } from './parsing/csvParser.ts';

type CsvImportDialogProps = {
    onClose: () => void;
    onOrdersCreated: () => void;
};

export const CsvImportDialog = ({ onClose, onOrdersCreated }: CsvImportDialogProps) => {
    const intl = useIntl();
    const [parent] = useAutoAnimate();
    const parseRequestId = useRef(0);
    const [selectedFileName, setSelectedFileName] = useState<string>();
    const [isParsingFile, setIsParsingFile] = useState(false);
    const [createOrder, { isLoading: createOrderLoading }] = useCreateOrderMutation();
    const dialogTitleId = 'intl-msg:ordercom-cloud-frontend.orders.csv-import.title';

    const formContext = useForm<ImportOrderFormData>({
        resolver: zodResolver(ImportOrderFormDataSchema),
        mode: 'onTouched',
        defaultValues: { orders: [] },
    });
    const { control, reset, handleSubmit } = formContext;
    const { fields } = useFieldArray({
        control,
        name: 'orders',
    });

    const submitImportedOrders = () => {
        return handleSubmit(
            async formValues => {
                const createdOrderIds: string[] = [];

                const requests = formValues.orders.map(async order => {
                    const writeOrder = apiSchema.WriteOrder.parse(mapWriteOrder(order));

                    const createdOrder = await createOrder(writeOrder).unwrap();
                    return createdOrderIds.push(createdOrder.id);
                });

                let atLeastOneOrderFailed = false;
                await Promise.allSettled(requests).then(results =>
                    results.forEach(result => {
                        if (result.status === 'rejected') {
                            console.log(result.reason);
                            atLeastOneOrderFailed = true;
                        }
                    })
                );
                if (atLeastOneOrderFailed) {
                    Notification.error(
                        intl.formatMessage({
                            id: 'intl-msg:ordercom-cloud-frontend.orders.csv-import.create-error',
                            defaultMessage: 'One or more orders could not be created.',
                        })
                    );
                }

                if (createdOrderIds.length > 0) {
                    Notification.success(
                        intl.formatMessage(
                            {
                                id: 'intl-msg:ordercom-cloud-frontend.orders.csv-import.create-success',
                                defaultMessage: '{count} orders imported successfully.',
                            },
                            { count: createdOrderIds.length }
                        )
                    );
                    onOrdersCreated();
                }
                reset({ orders: [] });
                onClose();
            },
            errors => {
                console.error(errors);
                Notification.error(
                    intl.formatMessage({
                        id: 'intl-msg:ordercom-cloud-frontend.orders.csv-import.validation-error',
                        defaultMessage:
                            'The orders could not be created. Please correct the errors in the form and try again.',
                    })
                );
            }
        );
    };

    const handleComplete = async () => {
        await submitImportedOrders()();
    };

    const handleCancel = () => {
        reset({ orders: [] });
        onClose();
    };

    const resetSelectedFile = () => {
        parseRequestId.current += 1;
        setSelectedFileName(undefined);
        setIsParsingFile(false);
        reset({ orders: [] });
    };

    const onFileSelected = async (file?: File) => {
        if (!file) {
            resetSelectedFile();
            return;
        }

        const requestId = parseRequestId.current + 1;
        parseRequestId.current = requestId;
        setSelectedFileName(file.name);
        setIsParsingFile(true);
        reset({ orders: [] });

        try {
            const content = await readFileContent(file);
            const orders = await parseCsvImport(content);

            if (parseRequestId.current !== requestId) {
                return;
            }
            reset(orders);
        } catch (error) {
            if (parseRequestId.current !== requestId) {
                return;
            }
            setSelectedFileName(undefined);
            reset({ orders: [] });
            reportErrorToSentry(error);
            Notification.error(
                intl.formatMessage({
                    id: 'intl-msg:ordercom-cloud-frontend.orders.csv-import.file-error',
                    defaultMessage: 'File could not be read. Please select a valid CSV file.',
                })
            );
        } finally {
            if (parseRequestId.current === requestId) {
                setIsParsingFile(false);
            }
        }
    };

    return (
        <FormProvider {...formContext}>
            <Dialog
                show={true}
                onClose={onClose}
                showCloseButton={false}
                data-testid='csv-import-dialog'
                headerButtons={
                    <Button
                        bsStyle={Button.MUTED}
                        iconOnly
                        iconName='remove'
                        autoTrackingKey={`${dialogTitleId}.close`}
                        className='modal-header-close close'
                        onClick={onClose}
                        aria-label='dialog close button'
                    />
                }
            >
                <Dialog.Title
                    title={intl.formatMessage({
                        id: dialogTitleId,
                        defaultMessage: 'Import orders from CSV',
                    })}
                />
                <Dialog.Body>
                    <div>
                        <CsvImportInstructions />
                        <CsvImportFilePicker
                            selectedFileName={selectedFileName}
                            onFileSelected={onFileSelected}
                            onReset={resetSelectedFile}
                        />
                    </div>

                    <div ref={parent}>
                        <div style={{ maxHeight: 420, overflow: 'auto' }}>
                            {fields.map((order, index) => (
                                <ImportOrderDetails
                                    key={`${order.number}_${order.id}`}
                                    control={control}
                                    index={index}
                                />
                            ))}
                        </div>
                    </div>
                </Dialog.Body>
                <Dialog.Footer>
                    <CsvImportDialogFooter
                        isCreatingOrders={createOrderLoading}
                        isSubmitDisabled={createOrderLoading || isParsingFile || fields.length === 0}
                        onCancel={handleCancel}
                        onSubmit={() => {
                            handleComplete().catch(reportErrorToSentry);
                        }}
                    />
                </Dialog.Footer>
            </Dialog>
        </FormProvider>
    );
};
